/**
 * ProductSibling - Size sibling selector
 *
 * Sizes are modelled as separate sibling products. Each click navigates to
 * the sibling product URL (e.g. /products/modern-sofa-large) via Section
 * Rendering API swap, with a full-page navigation fallback.
 *
 * All non-size options (Color, Configuration, Material, etc.) are native
 * Shopify variants and handled by <variant-selects>, not by this component.
 */

class ProductSibling extends HTMLElement {
  constructor() {
    super();
    this.productHandle = this.dataset.productHandle;
    this.productId = parseInt(this.dataset.productId, 10);
    this.sectionId = this.dataset.section;
  }

  connectedCallback() {
    try {
      const raw = this.dataset.metafields;
      if (!raw) { this.style.display = 'none'; return; }
      this.data = JSON.parse(raw);
    } catch (err) {
      console.error('ProductSibling: failed to parse metafields', err);
      return;
    }

    this.product = this.data.product || {};
    this.siblings = Array.isArray(this.data.siblings) ? this.data.siblings : [];

    this.render();
    this.bindEvents();
  }

  /* ----------------------------- Render ----------------------------- */
  render() {
    // Only render when there is more than one sibling (regardless of stock)
    if (this.siblings.length <= 1) {
      this.innerHTML = '';
      this.style.display = 'none';
      return;
    }

    this.innerHTML = `
      <div class="product-sibling">
        ${this.renderSizes()}
      </div>
    `;
  }

  renderSizes() {
    const siblingLabel = this.data.sibling_label || 'Size';
    const currentLabel = this.product.size_title || '';

    const cards = this.siblings.map((sib) => {
      const isActive = !!sib.is_current;
      const isUnavailable = sib.available === false;
      const image = sib.size_thumbnail || sib.featured_image || '';
      const title = sib.size_title || '';
      return `
        <button
          type="button"
          class="product-sibling__size-card${isActive ? ' is-active' : ''}${isUnavailable ? ' is-sold-out' : ''}"
          data-handle="${sib.handle}"
          aria-pressed="${isActive}"
          ${isActive ? 'aria-current="true"' : ''}
          ${isUnavailable ? 'aria-disabled="true"' : ''}
          title="${this.escape(title)}"
        >
          <span class="product-sibling__size-image">
            ${image ? `<img src="${image}" alt="${this.escape(title)}" title="${this.escape(title)}" loading="lazy">` : ''}
          </span>
          <span class="product-sibling__size-text">
            <span class="product-sibling__size-title">${this.escape(title)}</span>
          </span>
        </button>
      `;
    }).join('');

    return `
      <div class="product-sibling__section">
        <div class="product-sibling__label">
          ${this.escape(siblingLabel)}: <span class="product-sibling__current-name">${this.escape(currentLabel)}</span>
        </div>
        <div class="product-sibling__sizes">${cards}</div>
      </div>
    `;
  }

  /* ----------------------------- Events ----------------------------- */
  bindEvents() {
    this.querySelectorAll('[data-handle]').forEach((btn) => {
      btn.addEventListener('click', () => this.selectSibling(btn.dataset.handle));
    });
  }

  /* -------------------------- Sibling swap -------------------------- */
  async selectSibling(handle) {
    if (!handle || handle === this.productHandle) return;

    const sectionId = this.sectionId;
    const newUrl = `/products/${handle}`;

    if (!sectionId) {
      window.location.href = newUrl;
      return;
    }

    // The Section Rendering API fragment fetched below has no <head>/<title>,
    // so pass the destination sibling's server-computed Page Title (SEO title
    // tag) through to swapSection to update the browser tab. Uses the Page
    // Title only — not the product/sibling title — and leaves the tab unchanged
    // when no Page Title is set. Fixes DEV-115.
    const sibling = this.siblings.find((s) => s.handle === handle);
    const title = sibling ? (sibling.page_title || '') : '';

    await this.swapSection(newUrl, '', sectionId, { pushState: true, title });
  }

  /**
   * Fetch a product section via the Section Rendering API and swap it in place.
   * @param {string} productPath  e.g. "/products/my-sofa-large"
   * @param {string} queryString  e.g. "?variant=12345" (include the ?)
   * @param {string} sectionId    Shopify section id
   * @param {{ pushState?: boolean, title?: string }} opts
   */
  async swapSection(productPath, queryString, sectionId, opts = {}) {
    this.classList.add('is-loading');

    const sep = queryString ? `${queryString}&` : '?';
    const fetchUrl = `${productPath}${sep}section_id=${encodeURIComponent(sectionId)}`;

    try {
      const res = await fetch(fetchUrl, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(`Section fetch failed: ${res.status}`);
      const html = await res.text();

      const doc = new DOMParser().parseFromString(html, 'text/html');

      const targetSection = document.querySelector(`#shopify-section-${sectionId}`);
      const newSection = doc.querySelector(`#shopify-section-${sectionId}`) || doc.body;

      if (!targetSection || !newSection) throw new Error('Section element not found');

      targetSection.innerHTML = newSection.innerHTML;

      // Update URL — pushState so size change becomes a real history entry
      const newUrl = `${productPath}${queryString}`;
      if (opts.pushState) {
        window.history.pushState({}, '', newUrl);
      } else {
        window.history.replaceState({}, '', newUrl);
      }

      // A Section Rendering API response contains only the section markup, not
      // a <head>/<title>, so document.title cannot be read from the fetched
      // doc. Use the destination sibling's SEO title passed in via opts. DEV-115.
      if (opts.title) document.title = opts.title;

      this.executeScriptsIn(targetSection);

      document.dispatchEvent(new CustomEvent('flc:product:swapped', {
        bubbles: true,
        detail: { productPath, queryString, sectionId },
      }));
    } catch (err) {
      console.warn('ProductSibling: section swap failed, falling back to navigation', err);
      window.location.href = `${productPath}${queryString}`;
    } finally {
      this.classList.remove('is-loading');
    }
  }

  /**
   * Inline scripts inserted via innerHTML do not execute. Re-create them
   * so custom elements register and any inline init runs.
   */
  executeScriptsIn(root) {
    root.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  /* ------------------------------ Util ------------------------------ */
  escape(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

customElements.define('product-sibling', ProductSibling);